// Troca de senha (`POST /me/password`, Bearer + step-up; REQ-AUTH-003): validação local com zod, erros de negócio
// (`AUTH_PASSWORD_*`) do servidor via ProblemAlert. As senhas NUNCA são persistidas para retomar após o step-up:
// no retorno do provedor o usuário informa as senhas de novo (`resumedWithoutPayload`).
import type { NamespaceKey } from '@giyus/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Input } from '@giyus/ui';
import { useCallback, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { useStepUpAction } from '../../../lib/auth/useStepUp';
import { type ChangePasswordInput, useChangePassword } from '../api/accountQueries';
import { FormField } from '../components/FormField';
import {
  CHANGE_PASSWORD_ACTION,
  type ChangePasswordValues,
  PASSWORD_MIN_LENGTH,
  changePasswordSchema,
} from './changePasswordSchema';

export function ChangePasswordForm() {
  const { t } = useTranslation('identity');
  const change = useChangePassword();
  const [changed, setChanged] = useState(false);
  const ids = { current: useId(), next: useId(), confirm: useId() };
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const run = useCallback(
    async (input: ChangePasswordInput) => {
      setChanged(false);
      await change.mutateAsync(input);
      setChanged(true);
      reset();
    },
    [change, reset],
  );
  // Sem toPayload/fromPayload: segredos nunca vão ao sessionStorage (o usuário redigita após o step-up).
  const action = useStepUpAction<ChangePasswordInput>({ key: CHANGE_PASSWORD_ACTION, run });

  const translate = (message: string | undefined): string | undefined =>
    message ? t(message as NamespaceKey<'identity'>, { count: PASSWORD_MIN_LENGTH }) : undefined;

  const submit = handleSubmit((values) => {
    action
      .execute({ currentPassword: values.currentPassword, newPassword: values.newPassword })
      .catch(() => {
        // erro exibido pelo ProblemAlert (change.error)
      });
  });

  return (
    <form
      className="flex flex-col gap-3 rounded-card border p-4"
      data-testid="change-password-form"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <div>
        <h2 className="text-body font-semibold">{t('account.password.title')}</h2>
        <p className="text-body text-muted-foreground">{t('account.password.subtitle')}</p>
      </div>
      {action.resumedWithoutPayload && (
        <p role="status" className="text-body text-muted-foreground" data-testid="password-resumed">
          {t('account.password.resumed')}
        </p>
      )}
      <FormField
        id={ids.current}
        label={t('account.password.current')}
        error={translate(errors.currentPassword?.message)}
      >
        <Input
          id={ids.current}
          type="password"
          autoComplete="current-password"
          aria-invalid={Boolean(errors.currentPassword)}
          {...register('currentPassword')}
        />
      </FormField>
      <FormField
        id={ids.next}
        label={t('account.password.new')}
        hint={t('account.password.policyHint', { count: PASSWORD_MIN_LENGTH })}
        error={translate(errors.newPassword?.message)}
      >
        <Input
          id={ids.next}
          type="password"
          autoComplete="new-password"
          aria-invalid={Boolean(errors.newPassword)}
          {...register('newPassword')}
        />
      </FormField>
      <FormField
        id={ids.confirm}
        label={t('account.password.confirm')}
        error={translate(errors.confirmPassword?.message)}
      >
        <Input
          id={ids.confirm}
          type="password"
          autoComplete="new-password"
          aria-invalid={Boolean(errors.confirmPassword)}
          {...register('confirmPassword')}
        />
      </FormField>
      {change.isError && <ProblemAlert error={change.error} />}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={change.isPending} data-testid="change-password-submit">
          {t('account.password.submit')}
        </Button>
        {changed && (
          <p
            role="status"
            className="text-body text-muted-foreground"
            data-testid="password-changed"
          >
            {t('account.password.changed')}
          </p>
        )}
      </div>
    </form>
  );
}
